import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, X, Plus, Trash2 } from 'lucide-react';
import { Resource } from '../../lib/resources';
import { supabase } from '../../lib/supabase';
import { getServices, Service } from '../../lib/services';

interface CompensationSectionProps {
  resource: Resource;
  editMode: string | null;
  formData: Partial<Resource>;
  setFormData: (data: Partial<Resource>) => void;
  onSave: (section: string) => void;
  onEdit: (section: string) => void;
  loading?: boolean;
}

export default function CompensationSection({
  resource,
  editMode,
  formData,
  setFormData,
  onSave,
  onEdit,
  loading
}: CompensationSectionProps) {
  const [isBonusModalOpen, setIsBonusModalOpen] = useState(false);
  const [bookingBonusEnabled, setBookingBonusEnabled] = useState(false);
  const [bookingBonusPercentage, setBookingBonusPercentage] = useState<number>(0);
  
  // Service bonus state
  const [serviceBonusEnabled, setServiceBonusEnabled] = useState(false);
  const [serviceBonusItems, setServiceBonusItems] = useState<{serviceId: string, percentage: number}[]>([]);
  const [availableServices, setAvailableServices] = useState<Service[]>([]);

  // Load services when modal opens
  useEffect(() => {
    const loadServices = async () => {
      if (!isBonusModalOpen || !resource?.business_id) return;
      
      try {
        const services = await getServices(resource.business_id);
        setAvailableServices(services);
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    
    loadServices();
  }, [isBonusModalOpen, resource?.business_id]);

  // Load bonus configuration when modal opens
  useEffect(() => {
    const loadBonusConfiguration = async () => {
      if (!isBonusModalOpen || !resource?.id) return;
      
      try {
        const { data: dbResource, error } = await supabase
          .from('resources')
          .select('bonus_configuration')
          .eq('id', resource.id)
          .single();
        
        if (error) {
          console.error('Failed to load bonus configuration:', error);
          return;
        }
        
        const bonusConfig = dbResource?.bonus_configuration;
        const bookingBonus = bonusConfig?.booking_bonus;
        const serviceBonus = bonusConfig?.service_bonus;
        
        // Load booking bonus
        if (bookingBonus) {
          setBookingBonusEnabled(bookingBonus.enabled || false);
          setBookingBonusPercentage(bookingBonus.percentage || 0);
        } else {
          setBookingBonusEnabled(false);
          setBookingBonusPercentage(0);
        }
        
        // Load service bonus
        if (serviceBonus) {
          setServiceBonusEnabled(serviceBonus.enabled || false);
          setServiceBonusItems(serviceBonus.services || []);
        } else {
          setServiceBonusEnabled(false);
          setServiceBonusItems([]);
        }
      } catch (err) {
        console.error('Error loading bonus configuration:', err);
        setBookingBonusEnabled(false);
        setBookingBonusPercentage(0);
        setServiceBonusEnabled(false);
        setServiceBonusItems([]);
      }
    };
    
    loadBonusConfiguration();
  }, [resource?.id, isBonusModalOpen]);

  // Load bonus configuration for main display (regardless of modal state)
  useEffect(() => {
    const loadBonusConfigurationForDisplay = async () => {
      if (!resource?.id) return;
      
      try {
        const { data: dbResource, error } = await supabase
          .from('resources')
          .select('bonus_configuration')
          .eq('id', resource.id)
          .single();
        
        if (!error && dbResource?.bonus_configuration) {
          const bonusConfig = dbResource.bonus_configuration;
          
          // Load booking bonus for display
          if (bonusConfig.booking_bonus) {
            const bookingBonus = bonusConfig.booking_bonus;
            setBookingBonusEnabled(bookingBonus.enabled || false);
            setBookingBonusPercentage(bookingBonus.percentage || 0);
          }
          
          // Load service bonus for display  
          if (bonusConfig.service_bonus) {
            const serviceBonus = bonusConfig.service_bonus;
            setServiceBonusEnabled(serviceBonus.enabled || false);
            setServiceBonusItems(serviceBonus.services || []);
          }
        }
      } catch (err) {
        console.error('Error loading bonus configuration for display:', err);
      }
    };
    
    loadBonusConfigurationForDisplay();
  }, [resource?.id]);

  // Helper functions for service bonus management
  const addServiceBonusItem = () => {
    const availableService = availableServices.find(service => 
      !serviceBonusItems.some(item => item.serviceId === service.id)
    );
    
    if (availableService) {
      setServiceBonusItems([...serviceBonusItems, { 
        serviceId: availableService.id, 
        percentage: 0 
      }]);
    }
  };

  const removeServiceBonusItem = (index: number) => {
    setServiceBonusItems(serviceBonusItems.filter((_, i) => i !== index));
  };

  const updateServiceBonusPercentage = (index: number, percentage: number) => {
    const updatedItems = [...serviceBonusItems];
    updatedItems[index] = { ...updatedItems[index], percentage };
    setServiceBonusItems(updatedItems);
  };

  const updateServiceBonusService = (index: number, serviceId: string) => {
    const updatedItems = [...serviceBonusItems];
    updatedItems[index] = { ...updatedItems[index], serviceId };
    setServiceBonusItems(updatedItems);
  };

  const handleSaveBonusConfiguration = async () => {
    try {
      const bonusConfiguration = {
        ...resource?.bonus_configuration,
        booking_bonus: {
          enabled: bookingBonusEnabled,
          percentage: bookingBonusPercentage
        },
        service_bonus: {
          enabled: serviceBonusEnabled,
          services: serviceBonusItems
        }
      };

      // Update the database directly
      const { error: updateError } = await supabase
        .from('resources')
        .update({ bonus_configuration: bonusConfiguration })
        .eq('id', resource.id);
      
      if (updateError) {
        throw updateError;
      }
      // Close the modal
      setIsBonusModalOpen(false);
    } catch (error) {
      console.error('Error saving bonus configuration:', error);
    }
  };

  const bonusModalContent = (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div 
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Configure Bonuses</h2>
          <button
            onClick={() => setIsBonusModalOpen(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="space-y-6">
            {/* Active Bonuses Summary */}
            <div className="bg-gray-50 rounded-lg p-4 border">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Currently Active Bonuses</h3>
              <div className="space-y-2">
                {bookingBonusEnabled ? (
                  <div className="flex items-center justify-between py-2 px-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-green-800 font-medium">Booking Bonus</span>
                    </div>
                    <span className="text-sm text-green-700 font-semibold">{bookingBonusPercentage}%</span>
                  </div>
                ) : null}
                
                {serviceBonusEnabled && serviceBonusItems.length > 0 ? (
                  <div className="py-2 px-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-blue-800 font-medium">Service Bonuses</span>
                    </div>
                    <div className="space-y-1 ml-4">
                      {serviceBonusItems.map((item, index) => {
                        const service = availableServices.find(s => s.id === item.serviceId);
                        return (
                          <div key={index} className="flex items-center justify-between text-xs">
                            <span className="text-blue-700">{service?.name || 'Unknown Service'}</span>
                            <span className="text-blue-700 font-semibold">{item.percentage}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                
                {!bookingBonusEnabled && !serviceBonusEnabled && (
                  <div className="text-sm text-gray-500 italic py-2">
                    No active bonuses configured
                  </div>
                )}
              </div>
            </div>

            {/* Booking Bonus Section */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                {/* Checkbox */}
                <div className="flex items-center h-6 mt-1">
                  <input
                    id="booking-bonus"
                    type="checkbox"
                    checked={bookingBonusEnabled}
                    onChange={(e) => setBookingBonusEnabled(e.target.checked)}
                    className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <label htmlFor="booking-bonus" className="block">
                    <h3 className="text-base font-semibold text-gray-900 cursor-pointer">
                      Booking Bonus
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Earn a bonus on each booking scheduled
                    </p>
                  </label>
                  
                  {/* Percentage Input - Only show when enabled */}
                  {bookingBonusEnabled && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bonus Percentage
                      </label>
                      <div className="relative max-w-32">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={bookingBonusPercentage || ''}
                          onChange={(e) => setBookingBonusPercentage(parseFloat(e.target.value) || 0)}
                          className="w-full pl-3 pr-8 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200"
                          placeholder="0.0"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm">%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Service Bonus Section */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                {/* Checkbox */}
                <div className="flex items-center h-6 mt-1">
                  <input
                    id="service-bonus"
                    type="checkbox"
                    checked={serviceBonusEnabled}
                    onChange={(e) => setServiceBonusEnabled(e.target.checked)}
                    className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <label htmlFor="service-bonus" className="block">
                    <h3 className="text-base font-semibold text-gray-900 cursor-pointer">
                      Service Bonus
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Earn different bonus percentages based on specific services
                    </p>
                  </label>
                  
                  {/* Service Selection - Only show when enabled */}
                  {serviceBonusEnabled && (
                    <div className="mt-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">
                          Assign Services
                        </label>
                        <button
                          type="button"
                          onClick={addServiceBonusItem}
                          disabled={serviceBonusItems.length >= availableServices.length}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Service
                        </button>
                      </div>
                      
                      {/* Service Bonus Items */}
                      <div className="space-y-3">
                        {serviceBonusItems.map((item, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border">
                            {/* Service Selection */}
                            <div className="flex-1">
                              <select
                                value={item.serviceId}
                                onChange={(e) => updateServiceBonusService(index, e.target.value)}
                                className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200"
                              >
                                {availableServices.map((service) => (
                                  <option 
                                    key={service.id} 
                                    value={service.id}
                                    disabled={serviceBonusItems.some((otherItem, otherIndex) => 
                                      otherIndex !== index && otherItem.serviceId === service.id
                                    )}
                                  >
                                    {service.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            {/* Percentage Input */}
                            <div className="relative w-24">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={item.percentage || ''}
                                onChange={(e) => updateServiceBonusPercentage(index, parseFloat(e.target.value) || 0)}
                                className="w-full pl-3 pr-6 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200"
                                placeholder="0.0"
                              />
                              <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                                <span className="text-gray-500 text-xs">%</span>
                              </div>
                            </div>
                            
                            {/* Remove Button */}
                            <button
                              type="button"
                              onClick={() => removeServiceBonusItem(index)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        
                        {serviceBonusItems.length === 0 && (
                          <div className="text-sm text-gray-500 italic text-center py-4">
                            No services assigned. Click "Add Service" to get started.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={() => setIsBonusModalOpen(false)}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveBonusConfiguration}
            className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30 flex justify-between items-center">
        <h3 className="text-xs sm:text-base font-bold text-gray-900">Compensation</h3>
        {editMode === 'compensation' ? (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit('')}
              className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100/80 backdrop-blur-xl rounded-xl hover:bg-gray-200/80 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
              style={{ 
                minHeight: '32px',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => onSave('compensation')}
              disabled={loading}
              className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-white bg-purple-600 rounded-xl hover:bg-purple-700 disabled:opacity-50 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
              style={{ minHeight: '32px' }}
            >
              {loading ? (
                <>
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        ) : (
          <button
            onClick={() => onEdit('compensation')}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100/50 hover:bg-gray-200/50 text-gray-600 hover:text-gray-700 transition-all duration-200 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="p-2 sm:p-6">
        <div className="space-y-4 sm:space-y-6">
          {editMode === 'compensation' ? (
            <>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Compensation Type
                </label>
                <select
                  value={formData.compensation_type || 'hourly'}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    compensation_type: e.target.value as 'hourly' | 'flat_rate' | 'commission'
                  })}
                  className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    fontSize: '16px', // Prevent iOS zoom
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                >
                  <option value="hourly">Hourly Rate</option>
                  <option value="flat_rate">Flat Rate</option>
                  <option value="commission">Commission</option>
                </select>
              </div>

              {formData.compensation_type === 'hourly' && (
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Hourly Rate
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-base font-medium">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.hourly_rate || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        hourly_rate: parseFloat(e.target.value) 
                      })}
                      className="w-full pl-8 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                      style={{ 
                        minHeight: '44px',
                        fontSize: '16px', // Prevent iOS zoom
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                    />
                  </div>
                </div>
              )}

              {formData.compensation_type === 'flat_rate' && (
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Flat Rate Amount
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-base font-medium">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.flat_rate_amount || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        flat_rate_amount: parseFloat(e.target.value) 
                      })}
                      className="w-full pl-8 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                      style={{ 
                        minHeight: '44px',
                        fontSize: '16px', // Prevent iOS zoom
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                    />
                  </div>
                </div>
              )}

              {formData.compensation_type === 'commission' && (
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Commission Percentage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      value={formData.commission_percentage || formData.commission_rate || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        commission_percentage: parseFloat(e.target.value),
                        commission_rate: parseFloat(e.target.value)
                      })}
                      className="w-full pl-4 pr-8 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                      style={{ 
                        minHeight: '44px',
                        fontSize: '16px', // Prevent iOS zoom
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                    />
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-base font-medium">%</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">Compensation Type</div>
                <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold capitalize">
                  {resource.compensation_type.replace('_', ' ')}
                </div>
              </div>

              {resource.compensation_type === 'hourly' && (
                <div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Hourly Rate</div>
                  <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">
                    ${resource.hourly_rate.toFixed(2)}/hour
                  </div>
                </div>
              )}

              {resource.compensation_type === 'flat_rate' && (
                <div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Flat Rate Amount</div>
                  <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">
                    ${resource.flat_rate_amount.toFixed(2)}
                  </div>
                </div>
              )}

              {resource.compensation_type === 'commission' && (
                <div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Commission Rate</div>
                  <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">
                    {(resource.commission_percentage || resource.commission_rate || 0)}%
                  </div>
                </div>
              )}
              
              {/* Configure Bonuses Section */}
              <div className="border-t border-gray-200/50 pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-xs sm:text-sm text-gray-600 font-medium">Configure Bonuses</div>
                    <div className="text-xs text-gray-500 mt-0.5">Set up performance and milestone bonuses</div>
                    
                    {/* Active Bonuses Display */}
                    {(bookingBonusEnabled || serviceBonusEnabled) && (
                      <div className="mt-2 space-y-2">
                        {bookingBonusEnabled && (
                          <div className="inline-flex items-center space-x-2 py-1 px-2 bg-green-50 border border-green-200 rounded-lg">
                            <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                            <span className="text-xs text-green-800 font-medium">Booking Bonus</span>
                            <span className="text-xs text-green-700 font-semibold">{bookingBonusPercentage}%</span>
                          </div>
                        )}
                        {serviceBonusEnabled && serviceBonusItems.length > 0 && (
                          <div className="inline-flex items-center space-x-2 py-1 px-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                            <span className="text-xs text-blue-800 font-medium">Service Bonuses</span>
                            <span className="text-xs text-blue-700 font-semibold">
                              {serviceBonusItems.length} service{serviceBonusItems.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsBonusModalOpen(true)}
                    className="inline-flex items-center px-3 py-2 text-xs font-medium text-purple-600 bg-purple-50/80 backdrop-blur-sm border border-purple-200/50 rounded-xl hover:bg-purple-100/80 transition-all duration-200 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                    style={{ 
                      minHeight: '32px',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)'
                    }}
                  >
                    Configure
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Bonus Configuration Modal */}
      {isBonusModalOpen && createPortal(bonusModalContent, document.body)}
    </div>
  );
}